/**
 * ASCII Art Generator - Intervals 间隔条纹效果
 *
 * 双间隔条纹遮罩，两种间隔交替出现形成复杂图案
 */

import type { IntervalsConfig } from './types'
import { isVerticalDirection } from './helpers'

// ============================================
// Canvas 应用函数
// ============================================

/**
 * 应用间隔条纹效果到 Canvas
 * @param canvas 目标画布
 * @param config 间隔条纹配置
 */
export function applyIntervals(
  canvas: HTMLCanvasElement,
  config: IntervalsConfig
): void {
  const ctx = canvas.getContext('2d')!
  const { width, height } = canvas
  const { spacing1, width1, spacing2, width2, direction, animated, time } = config

  // 判断条纹方向
  const isVertical = isVerticalDirection(direction)

  // 动画偏移
  const offset1 = animated ? (time * 10) % spacing1 : 0
  const offset2 = animated ? (time * 10 * 0.7) % spacing2 : 0 // 第二条纹速度稍不同

  // 使用 destination-out 混合模式创建遮罩
  ctx.globalCompositeOperation = 'destination-out'

  // 绘制第一种条纹
  ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
  if (isVertical) {
    // 水平条纹
    for (let y = -spacing1 + offset1; y < height; y += spacing1) {
      ctx.fillRect(0, y, width, width1)
    }
  } else {
    // 垂直条纹
    for (let x = -spacing1 + offset1; x < width; x += spacing1) {
      ctx.fillRect(x, 0, width1, height)
    }
  }

  // 绘制第二种条纹（不同间距）
  ctx.fillStyle = 'rgba(0, 0, 0, 0.3)'
  if (isVertical) {
    // 水平条纹（偏移半个间距以形成交错效果）
    for (let y = -spacing2 / 2 + offset2; y < height; y += spacing2) {
      ctx.fillRect(0, y, width, width2)
    }
  } else {
    // 垂直条纹（偏移半个间距）
    for (let x = -spacing2 / 2 + offset2; x < width; x += spacing2) {
      ctx.fillRect(x, 0, width2, height)
    }
  }

  // 恢复默认混合模式
  ctx.globalCompositeOperation = 'source-over'
}

// ============================================
// 类封装（用于动画）
// ============================================

/**
 * 间隔条纹效果类
 */
export class IntervalsEffect {
  private config: IntervalsConfig
  private time: number = 0

  constructor(config: IntervalsConfig) {
    this.config = config
  }

  /**
   * 更新时间
   */
  update(deltaTime: number): void {
    if (this.config.animated) {
      this.time += deltaTime
    }
  }

  /**
   * 更新配置
   */
  updateConfig(config: IntervalsConfig): void {
    this.config = config
  }

  /**
   * 应用效果
   */
  apply(canvas: HTMLCanvasElement): void {
    const config = { ...this.config, time: this.time }
    applyIntervals(canvas, config)
  }

  /**
   * 重置时间
   */
  reset(): void {
    this.time = 0
  }
}

// ============================================
// 配置工厂函数
// ============================================

/**
 * 从用户设置创建间隔条纹配置
 */
export function createIntervalsConfigFromSettings(
  spacing1: number,
  width1: number,
  spacing2: number,
  width2: number,
  direction: string,
  animated: boolean
): IntervalsConfig {
  return {
    spacing1: Math.max(5, Math.min(50, Math.round(spacing1))),
    width1: Math.max(1, Math.min(10, Math.round(width1))),
    spacing2: Math.max(5, Math.min(50, Math.round(spacing2))),
    width2: Math.max(1, Math.min(10, Math.round(width2))),
    direction: direction as any,
    animated,
    time: 0
  }
}

// ============================================
// 默认配置
// ============================================

/** 默认间隔条纹配置 - 参数已调整使效果更明显 */
export const DEFAULT_INTERVALS_CONFIG: IntervalsConfig = {
  spacing1: 15,
  width1: 4,
  spacing2: 25,
  width2: 3,
  direction: 'down',
  animated: false,
  time: 0
}